// frontend/src/components/DrawerPanel.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Collapse,
  Grid,
  IconButton,
  ListItemIcon,
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  Button,
} from '@mui/material';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InputIcon from '@mui/icons-material/Input';
import FoundationIcon from '@mui/icons-material/Foundation';
import MapIcon from '@mui/icons-material/Map';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ConstructionIcon from '@mui/icons-material/Construction';
import HandshakeIcon from '@mui/icons-material/Handshake';
import BuildIcon from '@mui/icons-material/Build';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RuleIcon from '@mui/icons-material/Rule';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import 'react-toastify/dist/ReactToastify.css';

import { useLogoutMutation } from '../slices/usersApiSlice';
import { logout, setAppAccess } from '../slices/authSlice';
import { useGetItemsQuery } from '../slices/simpleApiSlice';
import { USERS_URL } from '../constans';
import { apiSlice } from '../slices/apiSlice'; 

const iconMap = {
  sales: <HandshakeIcon color='primary' />,
  mfc: <PrecisionManufacturingIcon color='secondary' />,
  cons: <ConstructionIcon color='success' />,
  stages: <WorkspacesIcon />,
};

const DrawerPanel = ({ setMobileOpen }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(null);

  // 1. Берем userInfo из Redux
  const { userInfo, permissions: reduxPermissions, sections: reduxSections } = useSelector((state) => state.auth);

  // 2. Делаем запрос к серверу.
  const { data, isSuccess } = useGetItemsQuery(
    { url: USERS_URL + '/drawer-menu' },
    { 
      skip: !userInfo, // 🛑 Блокируем запрос, пока нет данных юзера/токена
      refetchOnMountOrArgChange: true,
    }
  );

  // 3. Отправляем в Redux для других компонентов (опционально, лучше брать из RTKQ)
  useEffect(() => {
    if (isSuccess && data) {
      dispatch(
        setAppAccess({
          sections: data?.sections || [],
          permissions: data?.permissions || {},
        })
      );
    }
  }, [data, isSuccess, dispatch]);

  // 4. ГЛАВНОЕ: Для меню используем данные НАПРЯМУЮ из ответа сервера, 
  // а Redux выступает лишь фоллбэком.
  const activePermissions = data?.permissions || reduxPermissions || {};
  const activeSections = data?.sections || reduxSections || [];

  const [logoutOnTheServer] = useLogoutMutation();

  const logoutHandler = async () => {
    try {
      await logoutOnTheServer().unwrap();
    } catch (error) {
      console.error(error);
    }
    dispatch(logout());
    // 🛑 КРИТИЧЕСКИ ВАЖНО: При логауте очищаем кеш RTK Query! 
    dispatch(apiSlice.util.resetApiState()); 
    navigate('/auth');
  };

  const profileHandler = async () => {
    navigate('./profile');
  };

  const handleClick = (id) => {
    setOpen(!open);
    setOpenId(openId === id ? null : id);
  };

  const goto = (href, isOpen) => {
    navigate(href);
    setMobileOpen(false);
    if (!isOpen) {
      setOpen(false);
      setOpenId(null);
    }
  };

  // Вычисляем директории из activePermissions
  const directories = useMemo(() => {
    if (typeof activePermissions?.directories === 'object' && activePermissions.directories !== null) {
      return Object.keys(activePermissions.directories);
    }
    return [];
  }, [activePermissions]);

  // 5. Формируем меню
  const menu = useMemo(() => {
    const canReadNews = userInfo?.isAdmin || !!activePermissions?.news?.read;
    const canRunReports = userInfo?.isAdmin || !!activePermissions?.reports?.run;

    const directoriesMenuItems = [
      {
        id: 'projects',
        label: 'Клиенты',
        href: '/directories/projects',
        icon: <SwitchAccountIcon />,
        sx: { pl: 4 },
      },
      {
        id: 'stages',
        label: 'Этапы',
        href: '/directories/stages',
        icon: <ChecklistRtlIcon />,
        sx: { pl: 4 },
      },
      {
        id: 'types',
        label: 'Воронки',
        href: '/directories/types',
        icon: <FormatAlignCenterIcon />,
        sx: { pl: 4 },
      },
      {
        id: 'funnel-sections',
        label: 'Секции воронок',
        href: '/directories/funnel-sections',
        icon: <WorkspacesIcon />,
        sx: { pl: 4 },
      },
      {
        id: 'leadsources',
        label: 'Источники',
        href: '/directories/leadsources',
        icon: <InputIcon />,
        sx: { pl: 4 },
      },
      {
        id: 'regions',
        label: 'Регионы',
        href: '/directories/regions',
        icon: <MapIcon />,
        sx: { pl: 4 },
      },
      {
        id: 'building-projects',
        label: 'Проекты домов',
        href: '/directories/building-projects',
        icon: <FoundationIcon />,
        sx: { pl: 4 },
      },
      {
        id: 'equipments',
        label: 'Комплектации',
        href: '/directories/equipments',
        icon: <RuleIcon />,
        sx: { pl: 4 },
      },
      {
        id: 'reports',
        label: 'Отчеты',
        href: '/directories/reports',
        icon: <AssessmentIcon />,
        sx: { pl: 4 },
      },
      {
        id: 'financingtypes',
        label: 'Финансирование',
        href: '/directories/financingtypes',
        icon: <AccountBalanceWalletIcon />,
        sx: { pl: 4 },
      },
    ];

    let result = [];
    let availableSubMenu = directoriesMenuItems.filter((el) =>
      directories.includes(el.id),
    );

    if (['admin', 'админ'].includes(userInfo?.login)) {
      availableSubMenu.push(
        {
          id: 'tasks',
          label: 'Задачи',
          href: '/directories/tasks',
          icon: <AssignmentTurnedInIcon />,
          sx: { pl: 4 },
        },
        {
          id: 'taskstages',
          label: 'Статусы задач',
          href: '/directories/taskstages',
          icon: <ViewColumnIcon />,
          sx: { pl: 4 },
        },
        {
          id: 'users',
          label: 'Пользователи',
          href: '/users',
          icon: <PeopleIcon />,
          sx: { pl: 4 },
        },
        {
          id: 'admin',
          label: 'Коррекция',
          href: '/correction',
          icon: <BuildIcon />,
          sx: { pl: 4 },
        },
      );
    }

    if (availableSubMenu.length > 0) {
      result.push({
        id: 'admin',
        label: 'Настройки',
        icon: <CategoryOutlinedIcon />,
        children: availableSubMenu,
      });
    }

    if (!userInfo?.isAdmin) {
      result.push({
        id: 'profile',
        label: 'Мой профиль',
        href: '/profile',
        icon: <PeopleIcon />,
      });
    }

    // Рендерим секции ПРЯМО из актуальных данных
    if (activeSections.length > 0) {
      const sectionMenu = activeSections.map((section) => ({
        id: section.code,
        label: section.title,
        href: `/crm/${section?.code}`,
        icon: iconMap[section.code] || <WorkspacesIcon />,
      }));
      result = [...result, ...sectionMenu];
    }

    if (canRunReports)
      result.push({
        id: 'reports',
        label: 'Отчёты',
        href: '/reports',
        icon: <AssessmentIcon />,
      });
    if (canReadNews)
      result.push({
        id: 'news',
        label: 'Новости',
        href: '/news',
        icon: <HistoryIcon />,
      });

    result.push({
      id: 'tasks',
      label: 'Задачи',
      href: '/tasks',
      icon: <TaskAltIcon />,
    });

    return result;
  }, [directories, userInfo, activePermissions, activeSections]);

  // Подсветка активного пункта
  useEffect(() => {
    menu.forEach((root) => {
      if (Array.isArray(root?.children)) {
        root.children.forEach((child) => {
          if (pathname.startsWith(child.href)) {
            setOpenId(root.id);
            setSelected(child.id);
          }
        });
      }
    });
  }, [pathname, menu]);

  return (
    <React.Fragment>
      <Toolbar onClick={() => setMobileOpen(false)}>
        <Grid container alignItems='center' justifyContent='space-between'>
          <Grid item>
            <Button onClick={profileHandler}>
            <Typography variant='body1' noWrap component='div'>
              {userInfo?.name}
              </Typography>
              </Button>
          </Grid>
          <Grid item>
            <IconButton onClick={logoutHandler}>
              <LogoutIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Toolbar>
      <Divider />
      <List component='div' disablePadding>
        {menu.map((item) => (
          <React.Fragment key={item.label}>
            <ListItemButton
              selected={
                pathname === item.href || pathname.startsWith(item.href)
              }
              sx={{
                pl: 2,
                backgroundColor: openId === item.id && 'action.selected',
              }}
              onClick={() => {
                if (item?.children) {
                  handleClick(item.id);
                } else {
                  goto(item.href, false);
                }
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
              {Array.isArray(item?.children) &&
                (openId === item.id ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>

            {Array.isArray(item?.children) && item.children.length > 0 && (
              <Collapse in={openId === item.id} timeout='auto' unmountOnExit>
                <List component='div' disablePadding>
                  {item.children.map((child) => (
                    <ListItemButton
                      selected={
                        selected === child.id &&
                        (pathname === child.href ||
                          pathname.startsWith(child.href))
                      }
                      key={child.label}
                      sx={child?.sx}
                      onClick={() => goto(child.href)}
                    >
                      {child?.icon && <ListItemIcon>{child.icon}</ListItemIcon>}
                      <ListItemText primary={child.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
            <Divider />
          </React.Fragment>
        ))}
      </List>
    </React.Fragment>
  );
};

export default DrawerPanel;
